/**
 * Player survey copy and page layout — ground truth: docs/player-survey.md
 */
export const PLAYER_SURVEY_ID = 'player-v1' as const;
export const PLAYER_SURVEY_SCHEMA_VERSION = 1 as const;
export const PLAYER_SURVEY_PAGE_COUNT = 9 as const;

export const PLAYER_SURVEY_FRAMING =
  'Some quick questions about your experience with 10 Teams. Takes a few minutes. You can skip any question.';

export const PLAYER_SURVEY_Q8A_MAX_LEN = 300;
export const PLAYER_SURVEY_Q24_MAX_LEN = 4000;

export type SurveyAnswerValue = string | string[] | number;

/** Stable answer keys written to Firestore. */
export type PlayerSurveyAnswerKey =
  | 'q1'
  | 'q2'
  | 'q3'
  | 'q4'
  | 'q5'
  | 'q6'
  | 'q6a'
  | 'q6b'
  | 'q7'
  | 'q8'
  | 'q8a'
  | 'q9'
  | 'q10'
  | 'q11'
  | 'q12'
  | 'q13'
  | 'q14'
  | 'q15'
  | 'q16'
  | 'q17'
  | 'q18'
  | 'q19'
  | 'q20'
  | 'q21'
  | 'q22'
  | 'q23'
  | 'q24';

export const PLAYER_SURVEY_ANSWER_KEYS: readonly PlayerSurveyAnswerKey[] = [
  'q1',
  'q2',
  'q3',
  'q4',
  'q5',
  'q6',
  'q6a',
  'q6b',
  'q7',
  'q8',
  'q8a',
  'q9',
  'q10',
  'q11',
  'q12',
  'q13',
  'q14',
  'q15',
  'q16',
  'q17',
  'q18',
  'q19',
  'q20',
  'q21',
  'q22',
  'q23',
  'q24',
] as const;

export const PLAYER_SURVEY_CHECKBOX_KEYS = ['q10', 'q16'] as const;
export type PlayerSurveyCheckboxKey = (typeof PLAYER_SURVEY_CHECKBOX_KEYS)[number];

export type SurveyQuestionKind = 'radio' | 'checkbox' | 'shortText' | 'longText' | 'nps';

export interface SurveyQuestionDef {
  id: PlayerSurveyAnswerKey;
  prompt: string;
  kind: SurveyQuestionKind;
  options?: readonly string[];
  helperText?: string;
  /** Show only when answers[whenKey] === whenValue */
  whenKey?: PlayerSurveyAnswerKey;
  whenValue?: string;
}

export interface SurveyPageDef {
  pageIndex: number; // 1-based
  questions: readonly SurveyQuestionDef[];
}

const FEATURE_OPTIONS = [
  'Building / editing my 10-team list',
  'Competing in leagues',
  'Fixtures & results',
  'Knockout bracket',
  'World Cup map',
  'Stats',
  'Sharing my list with others',
  'AI League',
] as const;

export const PLAYER_SURVEY_PAGES: readonly SurveyPageDef[] = [
  {
    pageIndex: 1,
    questions: [
      {
        id: 'q1',
        prompt: 'How often have you visited the website during the World Cup?',
        kind: 'radio',
        options: [
          'Several times a day',
          'About once a day',
          'A few times a week',
          'About once a week',
          'Less often',
        ],
      },
      {
        id: 'q2',
        prompt: 'How do you use 10 Teams?',
        kind: 'radio',
        options: [
          'Mostly on my phone',
          'Mostly on a computer / laptop',
          'Roughly equally on phone and computer',
        ],
      },
      {
        id: 'q3',
        prompt: 'Which of these describes how you participate?',
        kind: 'radio',
        options: [
          'Just the Global League',
          'Competing in one private league',
          'Competing in several private leagues',
        ],
      },
      {
        id: 'q4',
        prompt: 'Have you created your own league on 10 Teams?',
        kind: 'radio',
        options: ['Yes — several', 'Yes — one', 'No', 'No — but I plan to in the future'],
      },
      {
        id: 'q5',
        prompt: 'Do you use the website to check fixtures, live scores, or results?',
        kind: 'radio',
        options: ['Yes', 'No'],
      },
    ],
  },
  {
    pageIndex: 2,
    questions: [
      {
        id: 'q6',
        prompt: 'Where are you based?',
        kind: 'radio',
        options: [
          'United Kingdom',
          'Europe',
          'North America',
          'South America',
          'Asia',
          'Africa',
          'Oceania',
        ],
      },
      {
        id: 'q6a',
        prompt: 'What is your gender?',
        kind: 'radio',
        options: ['Man', 'Woman', 'Other', 'Prefer not to say'],
      },
      {
        id: 'q6b',
        prompt: 'What is your age?',
        kind: 'radio',
        options: [
          '(0–10)',
          '(10–19)',
          '(20–29)',
          '(30–39)',
          '(40–49)',
          '(50–59)',
          '(60–69)',
          '(70–79)',
          '(80+)',
          'Prefer not to say',
        ],
      },
      {
        id: 'q7',
        prompt: 'Which tournament did you first participate in on 10 Teams?',
        kind: 'radio',
        options: [
          'World Cup 2022',
          'Women’s World Cup 2023',
          'Euro 2024',
          'Women’s Euros 2025',
          'World Cup 2026',
        ],
      },
      {
        id: 'q8',
        prompt: 'How did you first hear about 10 Teams?',
        kind: 'radio',
        options: ['Friend', 'Family', 'Colleague', 'Social Media', 'Other'],
      },
      {
        id: 'q8a',
        prompt: 'How did you hear about us?',
        kind: 'shortText',
        whenKey: 'q8',
        whenValue: 'Other',
      },
      {
        id: 'q9',
        prompt: 'Did someone specifically invite you to join a particular league?',
        kind: 'radio',
        options: ['Yes', 'No'],
      },
    ],
  },
  {
    pageIndex: 3,
    questions: [
      {
        id: 'q10',
        prompt: 'Which parts of 10 Teams do you enjoy or use most?',
        kind: 'checkbox',
        options: FEATURE_OPTIONS,
      },
      {
        id: 'q11',
        prompt: 'If you had to pick one favourite, which would it be?',
        kind: 'radio',
        options: [...FEATURE_OPTIONS, 'No single favourite'],
      },
    ],
  },
  {
    pageIndex: 4,
    questions: [
      {
        id: 'q12',
        prompt: 'Overall, how satisfied are you with the 10 Teams website?',
        kind: 'radio',
        options: ['Very satisfied', 'Satisfied', 'Mixed', 'Dissatisfied', 'Very dissatisfied'],
      },
      {
        id: 'q13',
        prompt: 'How much do you enjoy participating?',
        kind: 'radio',
        options: ['A lot', 'Quite a bit', 'Somewhat', 'Only a little', 'Not at all'],
      },
    ],
  },
  {
    pageIndex: 5,
    questions: [
      {
        id: 'q14',
        prompt: 'Have you already told or invited anyone else to join 10 Teams?',
        kind: 'radio',
        options: [
          'Yes — several people',
          'Yes — one or two people',
          'Not yet, but I plan to',
          'No, and I probably won’t',
        ],
      },
      {
        id: 'q15',
        prompt:
          'How likely are you to recommend / invite someone to participate in 10 Teams for future tournaments?',
        kind: 'nps',
      },
    ],
  },
  {
    pageIndex: 6,
    questions: [
      {
        id: 'q16',
        prompt: 'Which of these future tournaments would you be interested in playing on 10 Teams?',
        kind: 'checkbox',
        options: [
          'Women’s World Cup 2027',
          'Euro 2028',
          'Women’s Euro 2029',
          'World Cup 2030',
        ],
      },
      {
        id: 'q17',
        prompt: 'Would you open and read a daily newsletter during future tournaments?',
        kind: 'radio',
        options: ['Yes', 'Probably', 'Probably not', 'No'],
      },
    ],
  },
  {
    pageIndex: 7,
    questions: [
      {
        id: 'q18',
        prompt: 'How much would you value a 10 Teams mobile app?',
        kind: 'radio',
        options: [
          'A great deal — I’d use it a lot',
          'Quite a bit',
          'Somewhat',
          'Only a little',
          'Not at all — the website is enough',
          'Not sure',
        ],
      },
      {
        id: 'q19',
        prompt: 'If there were an app, would you prefer using the app or the website?',
        kind: 'radio',
        options: ['App', 'Website', 'Both', 'Not sure'],
      },
    ],
  },
  {
    pageIndex: 8,
    questions: [
      {
        id: 'q20',
        prompt:
          'Compared with other things you pay for around sport or entertainment, how would you describe the value of 10 Teams?',
        kind: 'radio',
        options: ['High value', 'Fair value', 'Low value'],
      },
      {
        id: 'q21',
        prompt: 'If 10 Teams were a paid product, what would you say it is worth to you?',
        kind: 'radio',
        options: [
          'Less than a coffee',
          'About a coffee',
          'About a streaming month',
          'More than that',
        ],
      },
      {
        id: 'q22',
        prompt:
          'Imagine 10 Teams charged a one-off fee per tournament to keep the site running. What would feel reasonable for you?',
        kind: 'radio',
        helperText: 'This is hypothetical — nothing is changing right now.',
        options: [
          '£0 — I wouldn’t pay',
          'Up to £2',
          'Up to £5',
          'Up to £10',
          'More than £10',
          'Not sure',
        ],
      },
      {
        id: 'q23',
        prompt:
          'If there were an optional way to support 10 Teams financially during a tournament, how likely would you be to contribute?',
        kind: 'radio',
        options: ['Very likely', 'Somewhat likely', 'Unlikely', 'Definitely not', 'Not sure'],
      },
    ],
  },
  {
    pageIndex: 9,
    questions: [
      {
        id: 'q24',
        prompt: 'Anything else you’d like to tell us?',
        kind: 'longText',
      },
    ],
  },
];

/** Flat map of question id → definition (for validation). */
export const PLAYER_SURVEY_QUESTION_BY_ID: Readonly<Record<PlayerSurveyAnswerKey, SurveyQuestionDef>> =
  Object.fromEntries(
    PLAYER_SURVEY_PAGES.flatMap((page) => page.questions.map((q) => [q.id, q])),
  ) as Record<PlayerSurveyAnswerKey, SurveyQuestionDef>;
